// RegulatoryInformationPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const RegulatoryInformationPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/legal' },
    { name: 'Regulatory Information', path: '/legal/regulations' },
  ];

  const regulatoryTopics = [
    {
      title: 'Environmental Regulations',
      description: 'We operate in strict compliance with environmental protection laws and regulations relevant to the scrap and recycling industry, ensuring responsible handling and processing of materials.',
      details: [
        'Waste management directives (e.g., WEEE, ELV).',
        'Emissions and pollution control standards.',
        'Hazardous waste disposal guidelines.',
      ],
    },
    {
      title: 'Trade & Export Regulations',
      description: 'Our platform adheres to international trade laws, customs regulations, and export controls to facilitate legitimate and compliant cross-border movement of scrap materials.',
      details: [
        'Basel Convention on hazardous waste.',
        'Import/export licensing requirements.',
        'Sanctions and embargo compliance.',
      ],
    },
    {
      title: 'Health & Safety Standards',
      description: 'We prioritize the health and safety of all individuals involved in the scrap supply chain. Our guidelines align with occupational safety standards to minimize risks.',
      details: [
        'Workplace safety protocols for scrap handling.',
        'Personal protective equipment (PPE) requirements.',
        'Emergency response procedures.',
      ],
    },
    {
      title: 'Anti-Money Laundering (AML) & KYC',
      description: 'To prevent illicit financial activities, we implement robust Anti-Money Laundering (AML) and Know Your Customer (KYC) policies, ensuring transparency in transactions.',
      details: [
        'Identity verification for all participants.',
        'Transaction monitoring for suspicious patterns.',
        'Reporting to relevant financial authorities.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Regulatory Information
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          We are committed to operating within a robust regulatory framework. This section provides an overview of the key regulations and compliance standards that govern our operations in the scrap and recycling industry.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {regulatoryTopics.map((topic, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {topic.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {topic.description}
              </Typography>

              <List dense disablePadding>
                {topic.details.map((detail, detailIndex) => (
                  <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default RegulatoryInformationPage;
